import React from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

// ==================== DATA ====================

// The tile images are decorative next to their headings, so they carry empty alt.
const tools = [
  {
    id: 'teachhub',
    title: 'TeachHub',
    image: '/assets/images/tech/teachhub.webp',
    body: (
      <>
        Portal for students to access all DOE applications that students use. This includes: i-Ready, HMH,
        Fundations, Google Classroom, and Clever. Within Clever students can access{' '}
        <a
          href="https://www.typing.com"
          target="_blank"
          rel="noopener noreferrer"
          className="font-semibold text-school-blue underline underline-offset-2 hover:text-school-goldDark focus:outline-none focus:ring-2 focus:ring-school-blue/40 rounded"
        >
          typing.com<span className="sr-only"> (opens in new window)</span>
        </a>{' '}
        and Me and Mine.
      </>
    ),
  },
  {
    id: 'google-classroom',
    title: 'Google Classroom',
    image: '/assets/images/tech/google-classroom.webp',
    body: 'This is where students will access their virtual classroom on days of remote learning. Assignments and meeting links will be posted within the Google Classroom.',
  },
  {
    id: 'nycsa',
    title: 'NYCSA',
    subtitle: 'NYC Schools Account',
    image: '/assets/images/tech/nycsa.webp',
    body: 'Parent portal to access student grades, attendance, test scores, and other important student information. Login with the username and password you used when you created the account.',
  },
];

const contacts = [
  { name: 'Ms. Berkowitz', email: 'mberkowitz4@schools.nyc.gov' },
  { name: 'Mr. Moore', email: 'rmoore7@schools.nyc.gov' },
];

// ==================== REUSABLE COMPONENTS ====================

const Container = ({ className = "", children }) => (
  <div className={`mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 ${className}`}>{children}</div>
);

const SectionHeader = ({ eyebrow, title, description, align = "left" }) => (
  <div className={align === "center" ? "text-center max-w-3xl mx-auto" : "max-w-3xl"}>
    {eyebrow && (
      <div className={align === "center" ? "flex justify-center" : ""}>
        <span className="inline-flex items-center gap-2 text-school-goldDark font-semibold tracking-[0.18em] uppercase text-xs">
          <span className="h-1.5 w-1.5 rounded-full bg-school-gold" aria-hidden="true" />
          {eyebrow}
        </span>
      </div>
    )}
    <h2 className="mt-3 text-3xl sm:text-4xl md:text-5xl font-heading font-bold text-school-blue leading-[1.08] tracking-tight">
      {title}
    </h2>
    {description && (
      <p className={`mt-4 text-base md:text-lg text-gray-600 leading-7 ${align === "center" ? "mx-auto" : ""}`}>
        {description}
      </p>
    )}
  </div>
);

const ToolCard = ({ title, subtitle, image, body }) => (
  <div className="flex flex-col items-center text-center p-8 bg-white rounded-3xl border border-gray-100 shadow-soft hover:shadow-md transition-all duration-300 hover:-translate-y-0.5">
    <img
      src={image}
      width={168}
      height={168}
      alt=""
      loading="lazy"
      className="w-28 h-28 rounded-[18%] shadow-sm mb-6"
    />
    <h3 className="text-xl md:text-2xl font-heading font-bold text-gray-900 tracking-tight leading-tight">
      {title}
    </h3>
    {subtitle && <p className="mt-1 text-sm font-semibold text-school-goldDark">{subtitle}</p>}
    <p className="mt-4 text-sm md:text-base text-gray-600 leading-7">{body}</p>
  </div>
);

const EnvelopeIcon = ({ className = "w-5 h-5" }) => (
  <svg className={className} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
  </svg>
);

const PhoneIcon = ({ className = "w-5 h-5" }) => (
  <svg className={className} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-2.896-1.596-5.48-4.18-7.076-7.076l1.293-.97c.362-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z" />
  </svg>
);

// ==================== MAIN COMPONENT ====================

const TechnologyResources = () => (
  <div className="min-h-screen font-sans text-gray-600 antialiased selection:bg-school-gold/30 selection:text-school-blue" style={{ backgroundColor: '#F5EFE0' }}>
    <Navbar />

    <main id="main-content">

      {/* ==================== HERO SECTION ==================== */}
      <section className="relative min-h-[50vh] md:min-h-[60vh] flex items-center overflow-hidden bg-[#0F2240]">
        <div className="absolute inset-0 opacity-10 bg-[radial-gradient(ellipse_at_top,rgba(255,255,255,0.35),transparent_55%)]" aria-hidden="true" />
        <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse 70% 50% at 50% 100%, rgba(197,145,50,0.1) 0%, transparent 70%)' }} />

        <Container className="relative z-10 pt-32 pb-16 md:pt-40 md:pb-20">
          <div className="max-w-4xl bg-[#0F2240]">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 backdrop-blur-md border border-white/20 text-white/90 text-xs sm:text-sm font-semibold mb-7">
              <span className="w-2 h-2 rounded-full bg-school-gold" aria-hidden="true" />
              Parent Portal
            </div>

            <h1 className="text-4xl sm:text-5xl md:text-6xl font-heading font-bold text-white tracking-tight leading-[1.05]">
              Technology Resources
            </h1>

            <p className="mt-6 text-lg md:text-xl text-gray-200 leading-relaxed max-w-3xl">
              The tools students and families use to learn, check progress, and stay connected
            </p>
          </div>
        </Container>
      </section>

      {/* ==================== TOOLS ==================== */}
      <section className="py-20 md:py-28 bg-[#F5EFE0]">
        <Container>
          <SectionHeader
            align="center"
            eyebrow="Student & Family Tools"
            title="Where to Log In"
            description="A quick guide to the accounts and apps you will use this year"
          />

          <div className="mt-14 grid md:grid-cols-3 gap-6 lg:gap-8">
            {tools.map(({ id, ...tool }) => (
              <ToolCard key={id} {...tool} />
            ))}
          </div>
        </Container>
      </section>

      {/* ==================== SUPPORT ==================== */}
      {/* Flat navy band (no decorative overlays) so white text sits on a surface
          an automated contrast check can decide. */}
      <section className="py-20 md:py-24 bg-[#0F2240]">
        <Container className="text-center">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-heading font-bold text-white tracking-tight leading-[1.08]">
            Questions About Technology?
          </h2>
          <p className="mt-5 text-base md:text-lg text-gray-200 leading-7 max-w-2xl mx-auto">
            If you have any questions about technology related issues, please contact either Ms. Berkowitz or
            Mr. Moore. You may also call the school and leave a message for either of them.
          </p>

          <div className="mt-10 flex flex-col sm:flex-row flex-wrap gap-4 justify-center">
            {contacts.map(({ name, email }) => (
              <a
                key={email}
                href={`mailto:${email}`}
                className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full bg-school-gold text-school-blue font-bold text-sm hover:bg-white transition-colors focus:outline-none focus:ring-2 focus:ring-school-gold focus:ring-offset-2 focus:ring-offset-[#0F2240]"
              >
                <EnvelopeIcon />
                Email {name}
              </a>
            ))}
            <a
              href="tel:+17185845275"
              className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full bg-[#0F2240] text-white border-2 border-white/30 font-bold text-sm hover:bg-white hover:text-school-blue transition-colors focus:outline-none focus:ring-2 focus:ring-school-gold focus:ring-offset-2 focus:ring-offset-[#0F2240]"
            >
              <PhoneIcon />
              Call (718) 584-5275
            </a>
          </div>
        </Container>
      </section>

    </main>

    <Footer />
  </div>
);

export default TechnologyResources;
